import { useEffect, useState } from "react";
import ScheduledGigsWorkspace, { type ScheduledGigCard } from "./ScheduledGigsWorkspace";
import { reviewsApi } from "../../lib/reviews";
import { adultScheduledGigsApi } from "../../lib/scheduledGigs";
import type { ScheduledGigItemResponse } from "../../types/scheduledGigTypes";
import { apiErrorToMessage } from "../../utils/ApiUtils";

const normalizeGig = (gig: ScheduledGigItemResponse): ScheduledGigCard => ({
  requestId: gig.requestId,
  gigId: gig.gigId,
  gigTitle: gig.gigTitle,
  gigDescription: gig.gigDescription,
  gigRequiresEquipment: gig.gigRequiresEquipment,
  gigEquipmentDescription: gig.gigEquipmentDescription,
  locationType: gig.locationType,
  city: gig.city,
  state: gig.state,
  zip: gig.zip,
  gigTimeZone: gig.gigTimeZone,
  participantName: [gig.participantFirstName, gig.participantLastName].filter(Boolean).join(" ") || "Teen",
  participantLabel: "Teen",
  paymentAmountCents: gig.paymentAmountCents,
  volunteer: gig.volunteer,
  teensRequired: Math.max(1, gig.teensRequired ?? 1),
  coveredSeats: Math.max(1, gig.coveredSeats ?? 1),
  confirmedStartTime: gig.confirmedStartTime,
  confirmedEndTime: gig.confirmedEndTime,
  scheduleProposals: gig.scheduleProposals ?? [],
  teenArrivedAt: gig.teenArrivedAt,
  teenMarkedCompletedAt: gig.teenMarkedCompletedAt,
  adultConfirmedCompletedAt: gig.adultConfirmedCompletedAt,
  issueReportedAt: gig.issueReportedAt,
  issueReportMessage: gig.issueReportMessage,
  paymentStatus: gig.paymentStatus,
  fundedAt: gig.fundedAt,
  releasedAt: gig.releasedAt,
  refundedAt: gig.refundedAt,
  removeTeenBlockingReason: gig.removeTeenBlockingReason,
});

const AdultScheduledGigsWorkspace = () => {
  const [gigs, setGigs] = useState<ScheduledGigItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadGigs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      setGigs(await adultScheduledGigsApi.list());
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to load your scheduled gigs."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadGigs();
  }, []);

  return (
    <ScheduledGigsWorkspace
      role="adult"
      title="Scheduled Gigs"
      subtitle="Review matched gigs, mark them complete when you are satisfied with the work, or report any issues that still need follow-up."
      loadingMessage="Loading your scheduled gigs..."
      emptyMessage="Once a gig is confirmed by both the teen and the adult, it will appear here."
      gigs={gigs.map(normalizeGig)}
      isLoading={isLoading}
      error={error}
      onAdultConfirmComplete={async (requestId) => {
        await adultScheduledGigsApi.markComplete(requestId);
        await loadGigs();
      }}
      onAdultSubmitReview={async (requestId, payload) => {
        await reviewsApi.create(requestId, payload);
      }}
      onReportIssue={async (requestId, message) => {
        await adultScheduledGigsApi.reportIssue(requestId, { message });
        await loadGigs();
      }}
      onCancel={async (requestId, note) => {
        await adultScheduledGigsApi.cancel(requestId, note ? { note } : undefined);
        await loadGigs();
      }}
      onRemoveTeen={async (requestId, note) => {
        await adultScheduledGigsApi.removeTeen(requestId, { note });
        await loadGigs();
      }}
    />
  );
};

export default AdultScheduledGigsWorkspace;
